import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { LockerSpirit } from '../../../../../core/models/lockerSpirit.model';
import { ApiService } from '../../../../../core/services/api.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';

const materialModules = [
  MatIconModule,
  MatDialogModule,
  MatButtonModule,
  MatProgressSpinner
]

@Component({
  selector: 'app-spirit-detail-locker-consumption-image-upload',
  standalone: true,
  imports: [TranslateModule, ...materialModules],
  templateUrl: './spirit-detail-locker-consumption-image-upload.component.html',
  styleUrls: ['./spirit-detail-locker-consumption-image-upload.component.scss']
})
export class SpiritDetailLockerConsumptionImageUploadComponent implements OnInit, OnDestroy {

  api = inject(ApiService);

  lockerSpirit: LockerSpirit | null = null;
  lockerUser: any = null;

  @ViewChild('videoElement') videoElement?: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') canvasElement?: ElementRef<HTMLCanvasElement>;

  videoStream?: MediaStream;
  capturedImage: string | null = null;

  isUploading = false;
  uploadError = false;

  camerasCount = 0;
  usingFrontCamera = false;

  isNative = Capacitor.isNativePlatform();

  constructor(
    private dialogRef: MatDialogRef<SpiritDetailLockerConsumptionImageUploadComponent>
  ) { }

  async ngOnInit(): Promise<void> {
    if (!this.isNative) {
      await this.checkCameras();
      this.startCamera();
    }
  }

  ngOnDestroy(): void {
    this.stopCamera();
  }

  private async checkCameras() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.camerasCount = devices.filter(d => d.kind === 'videoinput').length;
    } catch (err) {
      console.error('Error enumerando cámaras:', err);
      this.camerasCount = 1;
    }
  }

  async startCamera() {
    try {
      await new Promise(r => setTimeout(r));

      if (!this.videoElement) {
        console.error('No se encontró el elemento de video');
        return;
      }

      if (this.videoStream) {
        this.videoStream.getTracks().forEach(track => track.stop());
      }

      const constraints: MediaStreamConstraints = {
        video: { facingMode: this.usingFrontCamera ? 'user' : 'environment' },
        audio: false
      };

      this.videoStream = await navigator.mediaDevices.getUserMedia(constraints);
      this.videoElement.nativeElement.srcObject = this.videoStream;
      this.capturedImage = null;

    } catch (err) {
      console.error('Error al acceder a la cámara:', err);
    }
  }

  toggleCamera() {
    if (this.camerasCount > 1) {
      this.usingFrontCamera = !this.usingFrontCamera;
      this.startCamera();
    }
  }

  async capturePhoto() {
    if (this.isNative) {
      await this.capturePhotoNative();
    } else {
      this.capturePhotoWeb();
    }
  }

  async capturePhotoNative() {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        correctOrientation: true
      });

      this.capturedImage = image.dataUrl || null;

    } catch (err) {
      console.error("Error tomando foto en app:", err);
    }
  }

  capturePhotoWeb() {
    if (!this.videoElement || !this.canvasElement) return;

    const video = this.videoElement.nativeElement;
    const canvas = this.canvasElement.nativeElement;
    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    this.capturedImage = canvas.toDataURL('image/png');
    this.stopCamera();
  }

  stopCamera() {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
      this.videoStream = undefined;
    }
  }

  repeatPhoto() {
    this.stopCamera();
    this.capturedImage = null;
    this.startCamera();
  }

  uploadPhoto() {
    if (!this.capturedImage) return;

    const photoBlob = this.base64ToBlob(this.capturedImage);
    const formData = new FormData();
    formData.append('photo', photoBlob, 'consumption.png');

    this.isUploading = true;
    this.uploadError = false;

    this.api
      .uploadLockerConsumptionImage(this.lockerUser.code, this.lockerSpirit!.consumptions[this.lockerSpirit!.consumptions.length - 1].id, formData)
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.isUploading = false;
            this.dialogRef.close(res.path);
          }
        },
        error: (err) => {
          this.uploadError = true;
          this.isUploading = false;
        }
      });
  }

  closeDialog() {
    this.stopCamera();
    this.dialogRef.close();
  }

  private base64ToBlob(base64: string): Blob {
    const byteString = atob(base64.split(',')[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const intArray = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      intArray[i] = byteString.charCodeAt(i);
    }
    return new Blob([intArray], { type: 'image/png' });
  }

}
